// MAIN STEPS - Top-level flow steps
const STEPS = {
  EV_PROFILE: 1,
  // Conditional step. Shown ONLY when the charger has more than one
  // USABLE connector at the moment "Get Started" is tapped. One usable
  // connector is resolved automatically and this step is skipped
  // entirely, so the common single-connector journey is unchanged.
  // Asked once, before anything is chosen or created: "do you have an
  // account?". It sits ahead of CONNECTOR_SELECTION because the answer
  // changes which screens follow, and — more importantly — because a
  // guest session is created with user_id = NULL and NOTHING in the API
  // can bind a user to it afterwards. Ask later and signing in either
  // orphans a session or opens a second one on the same connector.
  ACCOUNT_CHOICE: 1.25,
  CONNECTOR_SELECTION: 1.5,
  CHARGE_SETUP: 2,
  VEHICLE_VERIFICATION: 2.5,  // PIN verification for recognized license plates (conditional step)
  // Conditional step. Shown ONLY when the backend's access outcome for the
  // charger and the verified vehicle is not a plain "continue": the public
  // card fallback, a denial, or a charger with no charging flow. It prints
  // what the backend said (greencharge.access-outcome.v1) and offers the
  // actions the backend listed; FLEET_SESSION and PUBLIC_SESSION never
  // come here.
  FLEET_ACCESS_RESULT: 2.75,
  AMOUNT_PICKER: 3,
  MAKE_PAYMENT: 4,
  CONNECT_CHARGER: 5,  // FIXED: Was 6, now 5 (PROCESS_PAYMENT removed from main flow)
  MONITOR_CHARGING: 6,  // FIXED: Was 7, now 6
  DISCONNECT_CHARGER: 7,  // FIXED: Was 8, now 7
  SUMMARY: 8  // FIXED: Was 9, now 8
};

// PAYMENT SUBSTEPS - Used within MAKE_PAYMENT step only
const PAYMENT_STEPS = {
  DETAILS: 'details',      // Card authorization form
  RECEIPT: 'receipt',      // Receipt information form
  PROCESSING: 'processing' // Payment processing/authorization in progress
};

// The account-choice first screen is behind ACCOUNT_CHOICE_ENABLED (see
// config/features.js). With it OFF the maps below route exactly as before the
// screen existed: EV_PROFILE and CONNECTOR_SELECTION go straight to
// CHARGE_SETUP, and back from either returns to EV_PROFILE.
import { ACCOUNT_CHOICE_ENABLED } from '../config/features.js';

// STEP_FORWARD - Defines next step for forward navigation
// This handles decimal steps (e.g., 2.5) correctly
const STEP_FORWARD = {
  1: ACCOUNT_CHOICE_ENABLED ? 1.25 : 2, // EV_PROFILE → ACCOUNT_CHOICE (EvProfile stashes its click-time connector routing first); flag off → CHARGE_SETUP
  1.25: 2,   // ACCOUNT_CHOICE → CHARGE_SETUP (App overrides to 1.5 when a connector choice is pending,
             //                                and to 3 for guests, who have no plate to enter)
  1.5: 2,    // CONNECTOR_SELECTION → CHARGE_SETUP (App overrides to 3 for guests)
  2: 3,      // CHARGE_SETUP → AMOUNT_PICKER (default, may be overridden to 2.5 for PIN verification)
  2.5: 3,    // VEHICLE_VERIFICATION → AMOUNT_PICKER
  2.75: 3,   // FLEET_ACCESS_RESULT → AMOUNT_PICKER ("Continue with card": the public card flow)
  3: 4,      // AMOUNT_PICKER → MAKE_PAYMENT
  4: 5,      // MAKE_PAYMENT → CONNECT_CHARGER
  5: 6,      // CONNECT_CHARGER → MONITOR_CHARGING
  6: 7,      // MONITOR_CHARGING → DISCONNECT_CHARGER
  7: 8,      // DISCONNECT_CHARGER → SUMMARY
  // 8 (SUMMARY) has no forward step - it's the final step
};

// STEP_BACK - Defines previous step for back navigation
// This handles decimal steps (e.g., 2.5) correctly
const STEP_BACK = {
  1.25: 1,   // ACCOUNT_CHOICE → EV_PROFILE (back never creates a session)
  1.5: ACCOUNT_CHOICE_ENABLED ? 1.25 : 1, // CONNECTOR_SELECTION → ACCOUNT_CHOICE (flag off → EV_PROFILE)
  2: ACCOUNT_CHOICE_ENABLED ? 1.25 : 1,   // CHARGE_SETUP → ACCOUNT_CHOICE (flag off → EV_PROFILE)
  2.5: 2,    // VEHICLE_VERIFICATION → CHARGE_SETUP
  2.75: 2,   // FLEET_ACCESS_RESULT → CHARGE_SETUP ("Select another vehicle")
  // AMOUNT_PICKER → CHARGE_SETUP for a driver who had a plate screen.
  // A guest never saw one, so App sends them back past it — reversing
  // into a screen they were deliberately spared is disorienting, and
  // the plate field would sit there inviting input the guest path does
  // not use.
  3: 2,      // AMOUNT_PICKER → CHARGE_SETUP (skip verification if it was shown)
  4: 3,      // MAKE_PAYMENT → AMOUNT_PICKER
  5: 4,      // CONNECT_CHARGER → MAKE_PAYMENT
  6: 5,      // MONITOR_CHARGING → CONNECT_CHARGER
  7: 6,      // DISCONNECT_CHARGER → MONITOR_CHARGING
  8: 7       // SUMMARY → DISCONNECT_CHARGER
};

export { STEPS, PAYMENT_STEPS, STEP_FORWARD, STEP_BACK };
